import { useEffect, useState } from 'react';
import { isInstanceInContinuityOrder } from './utils';
import { ScanResult } from '@/types/types';

const ErrorDisplayPopup = ({ instanceData, validatorJson, studyData }: any) => {
  const [validationResults, setValidationResults] = useState<ScanResult[]>([]);

  useEffect(() => {
    handleValidationCheck();
  }, []);

  const handleValidationCheck = () => {
    const result: {
      errorItems: {
        field?: string;
        currentValue?: any;
        expectedValue?: any;
        isGroupItemsPassed?: boolean;
      }[];
      successItems: {
        field?: string;
        currentValue?: any;
        expectedValue?: any;
        isGroupItemsPassed?: boolean;
      }[];
      isDuplicate?: boolean;
      scanType: string;
    }[] = [];

    const instanceDataInfo = studyData?.instances?.sort(
      (a: any, b: any) => a.InstanceNumber - b.InstanceNumber
    );
    const validatorScans = Object.keys(validatorJson?.scan || {});

    validatorScans.forEach((scanType: string) => {
      const scanConfig = validatorJson.scan[scanType];
      const groupInstanceCount = scanConfig?.group?.instanceNumber;
      const isInvalidDicom = isInstanceInContinuityOrder(
        instanceDataInfo,
        groupInstanceCount
      );

      let scanResult = {
        errorItems: [] as any[],
        successItems: [] as any[],
        isDuplicate: studyData.isDuplicate,
        scanType: scanType,
      };

      const unmatchedScan = instanceData?.unMatchedItems?.find(
        (item: any) => item.scanType === scanType
      );

      if (unmatchedScan) {
        unmatchedScan.unmatched?.forEach((element: any) => {
          Object.entries(instanceData?.metadata || {}).forEach(
            ([metadataKey, metadataValue]: any) => {
              if (metadataKey === element) {
                const matchData = scanConfig.match;
                matchData?.forEach((data: any) => {
                  if (data?.field === element) {
                    let expectedValue: any;
                    if (data?.field === 'SeriesDescription') {
                      const expectedVal = data?.value?.map(
                        (res: any) => res?.value
                      );
                      expectedValue = expectedVal?.join(',');
                    } else {
                      expectedValue = Array.isArray(data?.value)
                        ? data?.value?.join(',')
                        : data?.value;
                    }

                    scanResult.errorItems.push({
                      field: data?.field,
                      currentValue: metadataValue,
                      expectedValue: expectedValue,
                    });
                  }
                });
              }
            }
          );
        });
      }

      scanConfig.match?.forEach((matchRule: any) => {
        const metadataValue = instanceData?.metadata?.[matchRule.field];
        const isFieldInUnmatched = unmatchedScan?.unmatched?.includes(
          matchRule.field
        );

        if (!isFieldInUnmatched && metadataValue !== undefined) {
          let expectedValue: any;
          if (matchRule?.field === 'SeriesDescription') {
            const expectedVal = matchRule?.value?.map((res: any) => res?.value);
            expectedValue = expectedVal?.join(',');
          } else {
            expectedValue = Array.isArray(matchRule?.value)
              ? matchRule?.value?.join(',')
              : matchRule?.value;
          }

          scanResult.successItems.push({
            field: matchRule?.field,
            currentValue: metadataValue,
            expectedValue: expectedValue,
          });
        }
      });

      if (isInvalidDicom) {
        scanResult.errorItems.push({
          isGroupItemsPassed: false,
        });
      } else {
        scanResult.successItems.push({
          isGroupItemsPassed: true,
        });
      }

      result.push(scanResult);
    });

    setValidationResults(result);
  };

  return (
    <div className="px-4 py-4">
      <div className="font-bold text-2xl">Validation Scheme</div>
      {validationResults?.map((data: any) => (
        <div key={data?.scanType} className="mb-4">
          <div className="text-black font-bold text-lg mb-2">
            {data?.scanType}
          </div>
          {data?.successItems?.length > 0 && (
            <div className="mb-2">
              <div className="text-green-600 font-semibold text-sm mb-1">
                ✓ Passed Validations:
              </div>
              {data?.successItems?.map((item: any, index: number) => (
                <div
                  key={`success-${item?.field || index}`}
                  className="flex text-ellipsis whitespace-nowrap overflow-hidden ml-2"
                >
                  {item?.field && (
                    <>
                      <div className="text-green-600">
                        <span className="text-green-600">Valid</span>{' '}
                        {item?.field}{' '}
                      </div>
                      <div className="ml-1 text-green-600">
                        {' '}
                        Expected : {item?.expectedValue},
                      </div>
                      <div className="ml-3 text-green-600">
                        Got : {item?.currentValue ?? 'Nothing'}{' '}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {data?.successItems?.some(
                (item: any) => item?.isGroupItemsPassed
              ) && (
                <div className="text-green-600 ml-2">
                  Instance count validation passed
                </div>
              )}
            </div>
          )}

          {data?.errorItems?.length > 0 && (
            <div className="mb-2">
              <div className="text-black-600 font-semibold text-sm mb-1">
                <span className="text-yellow-500 text-xl">&#9888;</span>
                &nbsp;Validation Warnings:
              </div>
              {data?.errorItems?.map((item: any, index: number) => (
                <div
                  key={`error-${item?.field || index}`}
                  className="flex text-ellipsis whitespace-nowrap overflow-hidden ml-2"
                >
                  {item?.field && (
                    <>
                      <div className="text-black-600">
                        <span className="text-black-600">Invalid</span>{' '}
                        {item?.field}{' '}
                      </div>
                      <div className="ml-1 text-black-600">
                        {' '}
                        Expected : {item?.expectedValue},
                      </div>
                      <div className="ml-3 text-black-600">
                        Got : {item?.currentValue ?? 'Nothing'}{' '}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {data?.errorItems?.some(
                (item: any) => item?.isGroupItemsPassed
              ) && (
                <div className="text-black-600 ml-2">
                  ✗ Failed with Instance count
                </div>
              )}
            </div>
          )}
          {data?.errorItems?.length === 0 && (
            <div className="text-green-600 font-semibold">
              ✓ All validations passed for this scan type
            </div>
          )}
        </div>
      ))}

      {validationResults[0]?.isDuplicate && (
        <div className="text-black-600 mt-2 font-semibold">
          <span className="text-yellow-500 text-xl">&#9888;</span> Duplicate
          Data
        </div>
      )}
    </div>
  );
};

export default ErrorDisplayPopup;
